import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Table,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Descriptions,
  Tooltip,
  Button,
  Space,
  Radio,
  message,
  Modal,
  List,
  Pagination,
  Badge,
  Select,
  Alert,
  Checkbox,
  Divider,
} from 'antd';
import { useQuery } from 'react-query';
import { DocService } from '../../../services/dataService';
import SearchFilter from '../dashboard/components/filter';
import {
  TrophyOutlined,
  BarChartOutlined,
  FileTextOutlined,
  DownloadOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

const { Option } = Select;

const DrugScoringTable = () => {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportJobId, setExportJobId] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);
  const pollingIntervalRef = useRef(null);
  const [viewMode, setViewMode] = useState('basic'); // 'basic' or 'all'
  const [scoringFilters, setScoringFilters] = useState({});
  const [scoringSearch, setScoringSearch] = useState('');
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);

  // Selection state
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [selectedCancerType, setSelectedCancerType] = useState(null);
  const [availableDrugs, setAvailableDrugs] = useState([]);
  const [availableCancerTypes, setAvailableCancerTypes] = useState([]);
  const [exportModalVisible, setExportModalVisible] = useState(false);

  // Fetch available options on mount
  const { isLoading: optionsLoading } = useQuery(['drugScoringOptions'], () => DocService.getDrugScoringOptions(), {
    onSuccess: (data) => {
      setAvailableDrugs(data.drugs || []);
      setAvailableCancerTypes(data.cancer_types || []);
    },
  });

  // Fetch drug scoring aggregation data - when either drug OR cancer type is selected
  const { data, isLoading } = useQuery(
    ['drugScoringAgg', selectedDrug, selectedCancerType, scoringSearch, scoringFilters],
    () =>
      DocService.getDrugScoringAggregation({
        drug: selectedDrug || undefined,
        cancer_type: selectedCancerType || undefined,
        filters: JSON.stringify(scoringFilters),
        search: scoringSearch,
      }),
    {
      enabled: !!selectedDrug || !!selectedCancerType, // Fetch when either drug OR cancer type is selected
    },
  );
  const sortedPairs = useMemo(() => {
    const pairs = data?.pairs ? [...data.pairs] : [];
    return pairs.sort((a, b) => (b.relevant_rct_count || 0) - (a.relevant_rct_count || 0));
  }, [data]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination((prev) => ({
      current: 1,
      pageSize: prev.pageSize,
    }));
  }, [scoringSearch, scoringFilters, selectedDrug, selectedCancerType]);

  // Format study type tag
  const formatStudyType = (type) => {
    const colorMap = {
      trial_rct: 'green',
      trial_non_rct: 'blue',
      meta_analysis: 'purple',
      observational: 'orange',
      review: 'cyan',
      case_study: 'geekblue',
      preclinical: 'magenta',
      other: 'default',
    };
    return <Tag color={colorMap[type] || 'default'}>{type?.replace(/_/g, ' ').toUpperCase() || 'N/A'}</Tag>;
  };

  // Format therapeutic association tag
  const formatTherapeuticAssociation = (ta) => {
    const colorMap = {
      positive: 'success',
      negative: 'error',
      inconclusive: 'warning',
      not_mentioned: 'default',
    };
    return <Tag color={colorMap[ta] || 'default'}>{ta?.replace(/_/g, ' ').toUpperCase() || 'N/A'}</Tag>;
  };

  // Format evidence strength tag
  const formatEvidenceStrength = (strength) => {
    const colorMap = {
      strong_evidence: 'green',
      weak_evidence: 'orange',
      no_evidence: 'red',
    };
    return <Tag color={colorMap[strength] || 'default'}>{strength?.replace(/_/g, ' ').toUpperCase() || 'N/A'}</Tag>;
  };

  // Basic columns for simplified view
  const basicColumns = [
    {
      title: 'Drug',
      dataIndex: 'drug',
      key: 'drug',
      fixed: 'left',
      width: 150,
      sorter: (a, b) => a.drug.localeCompare(b.drug),
      render: (text) => (
        <a
          href={`/dashboard?drug=${encodeURIComponent(text)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1890ff', fontWeight: 'bold', cursor: 'pointer' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Cancer Type',
      dataIndex: 'cancer_type',
      key: 'cancer_type',
      width: 150,
      sorter: (a, b) => a.cancer_type.localeCompare(b.cancer_type),
      ellipsis: true,
      render: (text) => (
        <a
          href={`/dashboard?task_specific_details.nccn_cancer_types=${encodeURIComponent(text)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1890ff', cursor: 'pointer' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Is Cancer Generic',
      dataIndex: 'is_generic',
      key: 'is_generic',
      width: 120,
      sorter: (a, b) => {
        if (a.is_generic === b.is_generic) return 0;
        return a.is_generic ? -1 : 1;
      },
      render: (value) => <Tag color={value ? 'green' : 'volcano'}>{value ? 'Generic' : 'Non-generic'}</Tag>,
    },
    {
      title: 'Average Score',
      dataIndex: 'avg_clinical_relevant_score',
      key: 'avg_clinical_relevant_score',
      width: 150,
      sorter: (a, b) => a.avg_clinical_relevant_score - b.avg_clinical_relevant_score,
      render: (score) => <Tag color={score > 100 ? 'green' : score > 50 ? 'blue' : 'default'}>{score.toFixed(2)}</Tag>,
    },
    {
      title: 'All Relevant Articles',
      dataIndex: 'relevance_distribution',
      key: 'all_relevant_articles',
      width: 150,
      sorter: (a, b) => {
        const aCount = a.relevance_distribution?.relevant || 0;
        const bCount = b.relevance_distribution?.relevant || 0;
        return aCount - bCount;
      },
      render: (relevanceDist, record) => {
        const count = relevanceDist?.relevant || 0;
        return (
          <a
            href={`/dashboard?drug=${encodeURIComponent(record.drug)}&task_specific_details.nccn_cancer_types=${encodeURIComponent(record.cancer_type)}&is_relevant=true`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Tag icon={<FileTextOutlined />} color="green">
              {count}
            </Tag>
          </a>
        );
      },
    },
    {
      title: 'Relevant RCTs',
      dataIndex: 'relevant_rct_count',
      key: 'relevant_rct_count',
      width: 120,
      sorter: (a, b) => a.relevant_rct_count - b.relevant_rct_count,
      defaultSortOrder: 'descend',
      render: (count, record) => (
        <a
          href={`/dashboard?drug=${encodeURIComponent(record.drug)}&task_specific_details.nccn_cancer_types=${encodeURIComponent(record.cancer_type)}&study_type=Clinical Trial: RCT&is_relevant=true`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Tag icon={<FileTextOutlined />} color="blue">
            {count}
          </Tag>
        </a>
      ),
    },
  ];

  // Helper to get all unique study types from relevant articles (excluding RCT)
  const getAllStudyTypes = () => {
    const types = new Set();
    data?.pairs?.forEach((pair) => {
      Object.keys(pair.relevant_study_type_distribution || {}).forEach((type) => {
        if (type !== 'Clinical Trial: RCT') {
          types.add(type);
        }
      });
    });
    return Array.from(types);
  };

  // Main table columns (all columns) - generated dynamically based on data
  const allColumns = useMemo(() => {
    const studyTypes = getAllStudyTypes();

    return [
      {
        title: 'Drug',
        dataIndex: 'drug',
        key: 'drug',
        fixed: 'left',
        width: 150,
        sorter: (a, b) => a.drug.localeCompare(b.drug),
        render: (text) => (
          <a
            href={`/dashboard?drug=${encodeURIComponent(text)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1890ff', fontWeight: 'bold', cursor: 'pointer' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
            }}
          >
            {text}
          </a>
        ),
      },
      {
        title: 'Cancer Type',
        dataIndex: 'cancer_type',
        key: 'cancer_type',
        width: 150,
        sorter: (a, b) => a.cancer_type.localeCompare(b.cancer_type),
        ellipsis: true,
        render: (text) => (
          <a
            href={`/dashboard?task_specific_details.nccn_cancer_types=${encodeURIComponent(text)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1890ff', cursor: 'pointer' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
            }}
          >
            {text}
          </a>
        ),
      },
      {
        title: 'Is Cancer Generic',
        dataIndex: 'is_generic',
        key: 'is_generic',
        width: 120,
        sorter: (a, b) => {
          if (a.is_generic === b.is_generic) return 0;
          return a.is_generic ? -1 : 1;
        },
        render: (value) => <Tag color={value ? 'green' : 'volcano'}>{value ? 'Generic' : 'Non-generic'}</Tag>,
      },
      {
        title: 'Scores',
        children: [
          {
            title: 'Total',
            dataIndex: 'total_score',
            key: 'total_score',
            width: 120,
            sorter: (a, b) => a.total_score - b.total_score,
            render: (score) => (
              <Tooltip title={`Total Score: ${score?.toFixed ? score.toFixed(2) : score}`}>
                <Tag color={score > 1000 ? 'green' : score > 500 ? 'blue' : 'orange'}>
                  {score?.toFixed ? score.toFixed(2) : score}
                </Tag>
              </Tooltip>
            ),
          },
          {
            title: 'Average',
            dataIndex: 'avg_clinical_relevant_score',
            key: 'avg_score',
            width: 140,
            sorter: (a, b) => a.avg_clinical_relevant_score - b.avg_clinical_relevant_score,
            render: (score) => (
              <Tag color={score > 100 ? 'green' : score > 50 ? 'blue' : 'default'}>
                {score?.toFixed ? score.toFixed(2) : score}
              </Tag>
            ),
          },
          {
            title: 'Avg RCT',
            dataIndex: 'avg_rct_score',
            key: 'avg_rct_score',
            width: 140,
            sorter: (a, b) => a.avg_rct_score - b.avg_rct_score,
            render: (score) => (
              <Tag color={score > 100 ? 'green' : score > 50 ? 'blue' : 'default'}>
                {score?.toFixed ? score.toFixed(2) : score}
              </Tag>
            ),
          },
        ],
      },
      {
        title: 'Relevant Articles',
        dataIndex: 'relevance_distribution',
        key: 'relevant_articles',
        width: 130,
        sorter: (a, b) => {
          const aCount = a.relevance_distribution?.relevant || 0;
          const bCount = b.relevance_distribution?.relevant || 0;
          return aCount - bCount;
        },
        render: (relevanceDist, record) => {
          const count = relevanceDist?.relevant || 0;
          return (
            <a
              href={`/dashboard?drug=${encodeURIComponent(record.drug)}&task_specific_details.nccn_cancer_types=${encodeURIComponent(record.cancer_type)}&is_relevant=true`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Tag icon={<FileTextOutlined />} color="green">
                {count}
              </Tag>
            </a>
          );
        },
      },
      // Therapeutic Association breakdown for relevant articles
      {
        title: 'TA (Relevant)',
        children: [
          {
            title: 'Positive',
            dataIndex: 'relevant_ta_distribution',
            key: 'relevant_ta_positive',
            width: 90,
            sorter: (a, b) => {
              const aCount = a.relevant_ta_distribution?.positive || 0;
              const bCount = b.relevant_ta_distribution?.positive || 0;
              return aCount - bCount;
            },
            render: (dist, record) => {
              const count = dist?.positive || 0;
              return (
                <a
                  href={`/dashboard?drug=${encodeURIComponent(
                    record.drug,
                  )}&task_specific_details.nccn_cancer_types=${encodeURIComponent(
                    record.cancer_type,
                  )}&is_relevant=true&impact_type=${encodeURIComponent('Positive')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Tag color="green">{count}</Tag>
                </a>
              );
            },
          },
          {
            title: 'No Benefit / Inconclusive / Not mentioned',
            dataIndex: 'relevant_ta_distribution',
            key: 'relevant_ta_nonpositive',
            width: 180,
            sorter: (a, b) => {
              const aCount =
                (a.relevant_ta_distribution?.negative || 0) +
                (a.relevant_ta_distribution?.inconclusive || 0) +
                (a.relevant_ta_distribution?.not_mentioned || 0);
              const bCount =
                (b.relevant_ta_distribution?.negative || 0) +
                (b.relevant_ta_distribution?.inconclusive || 0) +
                (b.relevant_ta_distribution?.not_mentioned || 0);
              return aCount - bCount;
            },
            render: (dist, record) => {
              const count = (dist?.negative || 0) + (dist?.inconclusive || 0) + (dist?.not_mentioned || 0);
              return (
                <a
                  href={`/dashboard?drug=${encodeURIComponent(
                    record.drug,
                  )}&task_specific_details.nccn_cancer_types=${encodeURIComponent(
                    record.cancer_type,
                  )}&is_relevant=true&impact_type=${encodeURIComponent('No Benefit')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Tag color="blue">{count}</Tag>
                </a>
              );
            },
          },
        ],
      },
      // Study Type breakdown
      {
        title: 'Study Types',
        children: [
          // RCT with TA breakdown (relevant RCTs only)
          {
            title: 'RCT',
            children: [
              {
                title: 'Total',
                dataIndex: 'relevant_rct_count',
                key: 'relevant_rct_count',
                width: 70,
                sorter: (a, b) => a.relevant_rct_count - b.relevant_rct_count,
                defaultSortOrder: 'descend',
                render: (count) => <Tag color="green">{count}</Tag>,
              },
              {
                title: 'Positive',
                dataIndex: 'rct_ta_breakdown',
                key: 'rct_ta_positive',
                width: 90,
                sorter: (a, b) => {
                  const aCount = a.rct_ta_breakdown?.positive || 0;
                  const bCount = b.rct_ta_breakdown?.positive || 0;
                  return aCount - bCount;
                },
                render: (dist, record) => {
                  const count = dist?.positive || 0;
                  return (
                    <a
                      href={`/dashboard?drug=${encodeURIComponent(
                        record.drug,
                      )}&task_specific_details.nccn_cancer_types=${encodeURIComponent(
                        record.cancer_type,
                      )}&study_type=${encodeURIComponent(
                        'Clinical Trial: RCT',
                      )}&is_relevant=true&impact_type=${encodeURIComponent('Positive')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Tag color="cyan">{count}</Tag>
                    </a>
                  );
                },
              },
              {
                title: 'No Benefit / Inconclusive / Not mentioned',
                dataIndex: 'rct_ta_breakdown',
                key: 'rct_ta_nonpositive',
                width: 200,
                sorter: (a, b) => {
                  const aCount =
                    (a.rct_ta_breakdown?.negative || 0) +
                    (a.rct_ta_breakdown?.inconclusive || 0) +
                    (a.rct_ta_breakdown?.not_mentioned || 0);
                  const bCount =
                    (b.rct_ta_breakdown?.negative || 0) +
                    (b.rct_ta_breakdown?.inconclusive || 0) +
                    (b.rct_ta_breakdown?.not_mentioned || 0);
                  return aCount - bCount;
                },
                render: (dist, record) => {
                  const count = (dist?.negative || 0) + (dist?.inconclusive || 0) + (dist?.not_mentioned || 0);
                  return (
                    <a
                      href={`/dashboard?drug=${encodeURIComponent(
                        record.drug,
                      )}&task_specific_details.nccn_cancer_types=${encodeURIComponent(
                        record.cancer_type,
                      )}&study_type=${encodeURIComponent(
                        'Clinical Trial: RCT',
                      )}&is_relevant=true&impact_type=${encodeURIComponent('No Benefit')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Tag color="cyan">{count}</Tag>
                    </a>
                  );
                },
              },
            ],
          },
          // Other study types (excluding RCT, relevant articles only)
          ...studyTypes.map((studyType) => ({
            title: studyType.length > 10 ? studyType.substring(0, 10) + '...' : studyType,
            dataIndex: 'relevant_study_type_distribution',
            key: `relevant_study_type_${studyType}`,
            width: 120,
            sorter: (a, b) => {
              const aCount = a.relevant_study_type_distribution?.[studyType] || 0;
              const bCount = b.relevant_study_type_distribution?.[studyType] || 0;
              return aCount - bCount;
            },
            render: (dist) => {
              const count = dist?.[studyType] || 0;
              return <Tag color="purple">{count}</Tag>;
            },
          })),
        ],
      },
      {
        title: 'Year Buckets',
        children: [
          {
            title: 'Pre-2010',
            dataIndex: 'publication_year_distribution',
            key: 'older_pre2010',
            width: 110,
            sorter: (a, b) =>
              (a.publication_year_distribution?.older_pre2010 || 0) -
              (b.publication_year_distribution?.older_pre2010 || 0),
            render: (dist) => {
              const count = dist?.older_pre2010 || 0;
              return <Tag color="blue">{count}</Tag>;
            },
          },
          {
            title: '2010-2019',
            dataIndex: 'publication_year_distribution',
            key: 'modern_2010-2019',
            width: 110,
            sorter: (a, b) =>
              (a.publication_year_distribution?.['modern_2010-2019'] || 0) -
              (b.publication_year_distribution?.['modern_2010-2019'] || 0),
            render: (dist) => {
              const count = dist?.['modern_2010-2019'] || 0;
              return <Tag color="blue">{count}</Tag>;
            },
          },
          {
            title: '2020+',
            dataIndex: 'publication_year_distribution',
            key: 'recent_2020+',
            width: 110,
            sorter: (a, b) =>
              (a.publication_year_distribution?.['recent_2020+'] || 0) -
              (b.publication_year_distribution?.['recent_2020+'] || 0),
            render: (dist) => {
              const count = dist?.['recent_2020+'] || 0;
              return <Tag color="blue">{count}</Tag>;
            },
          },
        ],
      },
    ];
  }, [data]);

  // Filter columns based on view mode
  const mainColumns = useMemo(() => {
    return viewMode === 'basic' ? basicColumns : allColumns;
  }, [viewMode, allColumns]);

  // Top articles nested table columns
  const topArticlesColumns = [
    {
      title: 'PubMed ID',
      dataIndex: 'pubmed_id',
      key: 'pubmed_id',
      width: 120,
      render: (pmid) => (
        <a href={`https://pubmed.ncbi.nlm.nih.gov/${pmid}/`} target="_blank" rel="noopener noreferrer">
          {pmid}
        </a>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      width: 400,
    },
    {
      title: 'Score',
      dataIndex: ['scoring_meta', 'total_score'],
      key: 'score',
      width: 100,
      sorter: (a, b) => (a.scoring_meta?.total_score || 0) - (b.scoring_meta?.total_score || 0),
      render: (score) => <Tag color="blue">{score ? score.toFixed(2) : 'N/A'}</Tag>,
    },
    {
      title: 'Study Type',
      dataIndex: 'study_type',
      key: 'study_type',
      width: 150,
      render: formatStudyType,
    },
    {
      title: 'Year',
      dataIndex: 'published_year',
      key: 'published_year',
      width: 80,
      sorter: (a, b) => (a.published_year || 0) - (b.published_year || 0),
    },
    {
      title: 'Therapeutic Association',
      dataIndex: ['scoring_meta', 'therapeutic_association'],
      key: 'therapeutic_association',
      width: 150,
      render: formatTherapeuticAssociation,
    },
    {
      title: 'Evidence',
      dataIndex: ['scoring_meta', 'evidence_strength'],
      key: 'evidence_strength',
      width: 120,
      render: formatEvidenceStrength,
    },
    {
      title: 'Relevance',
      dataIndex: ['scoring_meta', 'relevance'],
      key: 'relevance',
      width: 100,
      render: (relevance) => (
        <Tag color={relevance === 'relevant' ? 'green' : 'default'}>{relevance?.toUpperCase() || 'N/A'}</Tag>
      ),
    },
  ];

  // Expandable row render - shows detailed distributions
  const expandedRowRender = (record) => {
    return (
      <Card className="mb-4">
        <Row gutter={[16, 16]}>
          {/* Score Statistics */}
          <Col span={24}>
            <Card title="Score Statistics" size="small" className="mb-4">
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic title="Average Score" value={record.average_score} precision={2} />
                </Col>
                <Col span={12}>
                  <Statistic title="Total Score" value={record.total_score} precision={2} />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={8}>
                  <Statistic title="All RCTs" value={record.rct_count} />
                </Col>
                <Col span={8}>
                  <Statistic title="Relevant RCTs" value={record.relevant_rct_count} />
                </Col>
                <Col span={8}>
                  <Statistic title="Positive RCTs" value={record.positive_score_rct_count} />
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Study Type Distribution (Relevant Articles Only) */}
          <Col span={24}>
            <Card title="Study Type Distribution (Relevant Articles Only)" size="small">
              <Descriptions column={1} size="small">
                {Object.entries(record.relevant_study_type_distribution || {}).map(([key, value]) => (
                  <Descriptions.Item key={key} label={formatStudyType(key)}>
                    <Tag color="cyan">{value}</Tag>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Card>
          </Col>

          {/* Therapeutic Association Distribution */}
          <Col span={8}>
            <Card title="Therapeutic Association" size="small">
              <Descriptions column={1} size="small">
                {Object.entries(record.therapeutic_association_distribution || {}).map(([key, value]) => (
                  <Descriptions.Item key={key} label={formatTherapeuticAssociation(key)}>
                    <Tag color="blue">{value}</Tag>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Card>
          </Col>

          {/* Evidence Strength Distribution */}
          <Col span={8}>
            <Card title="Evidence Strength" size="small">
              <Descriptions column={1} size="small">
                {Object.entries(record.evidence_strength_distribution || {}).map(([key, value]) => (
                  <Descriptions.Item key={key} label={formatEvidenceStrength(key)}>
                    <Tag color="blue">{value}</Tag>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Card>
          </Col>

          {/* Relevance Distribution */}
          <Col span={8}>
            <Card title="Relevance Distribution" size="small">
              <Descriptions column={1} size="small">
                {Object.entries(record.relevance_distribution || {}).map(([key, value]) => (
                  <Descriptions.Item
                    key={key}
                    label={<Tag color={key === 'relevant' ? 'green' : 'default'}>{key.toUpperCase()}</Tag>}
                  >
                    <Tag color="blue">{value}</Tag>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Card>
          </Col>

          {/* Publication Year Distribution */}
          <Col span={24}>
            <Card title="Publication Year Distribution" size="small">
              <Row gutter={[16, 16]}>
                {Object.entries(record.publication_year_distribution || {}).map(([key, value]) => (
                  <Col key={key} span={8}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: 8, color: '#666' }}>{key}</div>
                      <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                        {value}
                      </Tag>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>

          {/* Top Articles Table */}
          <Col span={24}>
            <Card
              title={
                <span>
                  <TrophyOutlined /> Top Articles by Score
                </span>
              }
              size="small"
            >
              <Table
                columns={topArticlesColumns}
                dataSource={record.top_articles || []}
                pagination={false}
                size="small"
                rowKey={(record) => record.pubmed_id}
                scroll={{ x: 1200 }}
              />
            </Card>
          </Col>
        </Row>
      </Card>
    );
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Poll for export status
  useEffect(() => {
    if (!exportJobId) {
      return;
    }

    const pollStatus = async () => {
      try {
        const status = await DocService.getExportStatus(exportJobId);
        setExportStatus(status);

        if (status.status === 'completed') {
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          // Download the file
          try {
            await DocService.downloadExport(exportJobId);
            message.success('Export downloaded successfully!');
            setIsExporting(false);
            setExportJobId(null);
            setExportStatus(null);
          } catch (downloadError) {
            console.error('Download failed:', downloadError);
            message.error('Failed to download export file');
            setIsExporting(false);
            setExportJobId(null);
            setExportStatus(null);
          }
        } else if (status.status === 'failed') {
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          message.error(`Export failed: ${status.error_message || 'Unknown error'}`);
          setIsExporting(false);
          setExportJobId(null);
          setExportStatus(null);
        }
        // If status is 'pending' or 'processing', continue polling
      } catch (error) {
        console.error('Error checking export status:', error);
        // Continue polling even on error (might be temporary)
      }
    };

    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(pollStatus, 2000);

    // Initial poll
    pollStatus();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [exportJobId]);

  // Export to Excel functionality - opens modal
  const handleExportClick = () => {
    setExportModalVisible(true);
  };

  // Export confirmation handler
  const handleExportConfirm = async (selectedPairs, exportAll, includeAbstracts = false) => {
    setExportModalVisible(false);
    setIsExporting(true);
    setExportStatus(null);

    try {
      const params = {
        filters: JSON.stringify(scoringFilters),
        search: scoringSearch,
      };

      if (exportAll) {
        params.export_all = 'true';
      } else if (selectedPairs && selectedPairs.length > 0) {
        params.selected_pairs = JSON.stringify(selectedPairs);
        params.export_all = 'false';
      } else {
        message.error('Please select at least one drug-cancer pair to export');
        setIsExporting(false);
        return;
      }

      // Add include_abstracts parameter
      if (includeAbstracts) {
        params.include_abstracts = 'true';
      } else {
        params.include_abstracts = 'false';
      }

      const result = await DocService.exportDrugScoringAggregation(params);

      if (result.job_id) {
        setExportJobId(result.job_id);
        message.info("Export started. You will be notified when it's ready.");
      } else {
        throw new Error('No job_id returned');
      }
    } catch (error) {
      console.error('Export failed:', error);
      message.error(error.message || 'Failed to start export. Please try again.');
      setIsExporting(false);
      setExportJobId(null);
      setExportStatus(null);
    }
  };

  return (
    <div className="p-4">
      <Card
        title={
          <Space>
            <BarChartOutlined />
            <span>Drug-Cancer Scoring Aggregation</span>
          </Space>
        }
        extra={
          <Space>
            <Radio.Group
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="basic">Basic</Radio.Button>
              <Radio.Button value="all">All</Radio.Button>
            </Radio.Group>
            <SearchFilter
              search={scoringSearch}
              setSearch={setScoringSearch}
              filters={scoringFilters}
              setFilters={setScoringFilters}
            />
            <Button icon={<HistoryOutlined />} onClick={() => setHistoryVisible(true)}>
              Export History
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportClick}
              loading={isExporting}
              disabled={isLoading || isExporting}
            >
              {isExporting && exportStatus?.status === 'processing'
                ? 'Processing...'
                : isExporting && exportStatus?.status === 'pending'
                  ? 'Starting...'
                  : 'Export Excel'}
            </Button>
          </Space>
        }
      >
        {/* Drug/Cancer Selection UI */}
        <div style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space>
              <span style={{ fontWeight: 'bold', minWidth: 100 }}>Select Drug:</span>
              <Select
                placeholder="Select a drug (optional)"
                style={{ width: 300 }}
                value={selectedDrug}
                onChange={(value) => {
                  setSelectedDrug(value);
                }}
                loading={optionsLoading}
                showSearch
                allowClear
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              >
                {availableDrugs.map((drug) => (
                  <Option key={drug} value={drug}>
                    {drug}
                  </Option>
                ))}
              </Select>
            </Space>
            <Space>
              <span style={{ fontWeight: 'bold', minWidth: 100 }}>Select Cancer Type:</span>
              <Select
                placeholder="Select a cancer type (optional)"
                style={{ width: 300 }}
                value={selectedCancerType}
                onChange={setSelectedCancerType}
                allowClear
                showSearch
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              >
                {availableCancerTypes.map((cancerType) => (
                  <Option key={cancerType} value={cancerType}>
                    {cancerType}
                  </Option>
                ))}
              </Select>
            </Space>
            <Space>
              <Button
                onClick={() => {
                  setSelectedDrug(null);
                  setSelectedCancerType(null);
                }}
                disabled={!selectedDrug && !selectedCancerType}
              >
                Clear All
              </Button>
            </Space>
          </Space>
        </div>

        {/* Show message when neither drug nor cancer type is selected */}
        {!selectedDrug && !selectedCancerType && (
          <Alert
            message="Please select either a drug or a cancer type to view scoring data"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Search and Filters */}
        {(selectedDrug || selectedCancerType) && (
          <div style={{ marginBottom: 16 }}>
            <SearchFilter
              search={scoringSearch}
              setSearch={setScoringSearch}
              filters={scoringFilters}
              setFilters={setScoringFilters}
            />
          </div>
        )}

        {/* Main Data Table */}
        {(selectedDrug || selectedCancerType) && (
          <Table
            columns={mainColumns}
            dataSource={sortedPairs}
            loading={isLoading}
            onChange={(paginationInfo) => {
              // Update pagination state
              setPagination({
                current: paginationInfo.current,
                pageSize: paginationInfo.pageSize,
              });
            }}
            rowKey={(record) => `${record.drug}_${record.cancer_type}`}
            expandable={{
              expandedRowRender,
              expandedRowKeys,
              onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} pairs`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            scroll={{ x: viewMode === 'basic' ? 1000 : 1800 }}
            bordered
            size="middle"
          />
        )}
      </Card>

      {/* Export Selection Modal */}
      <ExportSelectionModal
        visible={exportModalVisible}
        onClose={() => setExportModalVisible(false)}
        onExport={handleExportConfirm}
        availableDrugs={availableDrugs}
        availableCancerTypes={availableCancerTypes}
      />

      {/* Export History Modal */}
      <ExportHistoryModal
        visible={historyVisible}
        onClose={() => {
          setHistoryVisible(false);
          setHistoryPage(1);
        }}
        page={historyPage}
        onPageChange={setHistoryPage}
      />
    </div>
  );
};

// Export Selection Modal Component
const ExportSelectionModal = ({ visible, onClose, onExport, availableDrugs, availableCancerTypes }) => {
  const [exportMode, setExportMode] = useState('selected'); // 'selected' or 'all'
  const [selectedPairs, setSelectedPairs] = useState([{ drug: '', cancer_type: null }]);
  const [includeAbstracts, setIncludeAbstracts] = useState(false);

  const handleAddPair = () => {
    setSelectedPairs([...selectedPairs, { drug: '', cancer_type: null }]);
  };

  const handleRemovePair = (index) => {
    if (selectedPairs.length > 1) {
      setSelectedPairs(selectedPairs.filter((_, i) => i !== index));
    }
  };

  const handlePairChange = (index, field, value) => {
    const updated = [...selectedPairs];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedPairs(updated);
  };

  const handleOk = () => {
    if (exportMode === 'all') {
      onExport([], true, includeAbstracts);
    } else {
      // Validate selected pairs
      const validPairs = selectedPairs.filter((pair) => pair.drug);
      if (validPairs.length === 0) {
        message.error('Please select at least one drug-cancer pair');
        return;
      }
      // Normalize pairs (remove empty cancer_type)
      const normalizedPairs = validPairs.map((pair) => ({
        drug: pair.drug,
        cancer_type: pair.cancer_type || null,
      }));
      onExport(normalizedPairs, false, includeAbstracts);
    }
  };

  const handleCancel = () => {
    setExportMode('selected');
    setSelectedPairs([{ drug: '', cancer_type: null }]);
    setIncludeAbstracts(false);
    onClose();
  };

  return (
    <Modal
      title="Select Drug-Cancer Pairs to Export"
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={800}
      okText="Export"
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Radio.Group value={exportMode} onChange={(e) => setExportMode(e.target.value)} style={{ width: '100%' }}>
          <Radio value="all">Export All Drug-Cancer Pairs</Radio>
          <Radio value="selected">Select Specific Drug-Cancer Pairs</Radio>
        </Radio.Group>

        {exportMode === 'all' && (
          <Alert
            message="This will export all drug-cancer pairs matching your current filters. This may take a while for large datasets."
            type="warning"
            showIcon
          />
        )}

        {exportMode === 'selected' && (
          <div>
            <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Select Drug-Cancer Pairs:</div>
            {selectedPairs.map((pair, index) => (
              <div key={index} style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                <Select
                  placeholder="Select drug"
                  style={{ flex: 1 }}
                  value={pair.drug || undefined}
                  onChange={(value) => handlePairChange(index, 'drug', value)}
                  showSearch
                  filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                >
                  {availableDrugs.map((drug) => (
                    <Option key={drug} value={drug}>
                      {drug}
                    </Option>
                  ))}
                </Select>
                <Select
                  placeholder="All cancer types"
                  style={{ flex: 1 }}
                  value={pair.cancer_type || undefined}
                  onChange={(value) => handlePairChange(index, 'cancer_type', value)}
                  allowClear
                  showSearch
                  filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                >
                  <Option value={null}>All cancer types</Option>
                  {availableCancerTypes.map((cancerType) => (
                    <Option key={cancerType} value={cancerType}>
                      {cancerType}
                    </Option>
                  ))}
                </Select>
                <Button
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemovePair(index)}
                  disabled={selectedPairs.length === 1}
                  danger
                />
              </div>
            ))}
            <Button icon={<PlusOutlined />} onClick={handleAddPair} style={{ width: '100%', marginTop: 8 }}>
              Add Another Pair
            </Button>
          </div>
        )}

        <Divider />

        <Checkbox checked={includeAbstracts} onChange={(e) => setIncludeAbstracts(e.target.checked)}>
          Include Abstracts Sheet (unique PMID-abstract pairs)
        </Checkbox>
      </Space>
    </Modal>
  );
};

// Export History Modal Component
const ExportHistoryModal = ({ visible, onClose, page, onPageChange }) => {
  const { data, isLoading } = useQuery(['exportHistory', page], () => DocService.getExportHistory(page, 10), {
    enabled: visible,
  });
  const [downloadingJobId, setDownloadingJobId] = useState(null);

  const handleDownload = async (jobId) => {
    setDownloadingJobId(jobId);
    try {
      await DocService.downloadExport(jobId);
      message.success('Export downloaded successfully!');
    } catch (error) {
      console.error('Download failed:', error);
      message.error('Failed to download export file');
    } finally {
      setDownloadingJobId(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'processing':
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      default:
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { status: 'success', text: 'Completed' },
      failed: { status: 'error', text: 'Failed' },
      processing: { status: 'processing', text: 'Processing' },
      pending: { status: 'default', text: 'Pending' },
    };
    const config = statusConfig[status] || { status: 'default', text: status };
    return <Badge status={config.status} text={config.text} />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Modal
      title={
        <Space>
          <HistoryOutlined />
          <span>Export History</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      style={{ top: 20 }}
      bodyStyle={{ padding: '16px', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 200px)' }}
    >
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <List
          loading={isLoading}
          dataSource={data?.results || []}
          renderItem={(item) => (
            <List.Item
              actions={[
                item.status === 'completed' ? (
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownload(item.job_id)}
                    loading={downloadingJobId === item.job_id}
                    disabled={downloadingJobId !== null}
                  >
                    Download
                  </Button>
                ) : null,
              ].filter(Boolean)}
            >
              <List.Item.Meta
                avatar={getStatusIcon(item.status)}
                title={
                  <Space>
                    {getStatusBadge(item.status)}
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      {item.filename || `Export ${item.job_id.slice(0, 8)}`}
                    </span>
                  </Space>
                }
                description={
                  <div>
                    <div>Created: {formatDate(item.created_at)}</div>
                    {item.completed_at && <div>Completed: {formatDate(item.completed_at)}</div>}
                    {item.error_message && (
                      <div style={{ color: '#ff4d4f', marginTop: '4px' }}>Error: {item.error_message}</div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
        {data && data.total === 0 && !isLoading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No export history found</div>
        )}
      </div>
      {data && data.total > 0 && (
        <div style={{ marginTop: '16px', textAlign: 'right', flexShrink: 0 }}>
          <Pagination
            current={page}
            total={data.total}
            pageSize={10}
            onChange={onPageChange}
            showTotal={(total) => `Total ${total} exports`}
          />
        </div>
      )}
    </Modal>
  );
};

export default DrugScoringTable;
